/**
 * Plantillas de mensajes WhatsApp predefinidas por industria.
 * Cada usuario puede copiar estas plantillas y personalizarlas en su base de conocimiento.
 */

export interface PlantillaWhatsapp {
  id: string;
  emoji: string;
  industria: string;
  nombre: string;
  categoria: string; // 'bienvenida' | 'oferta' | 'confirmacion' | 'seguimiento' | 'soporte'
  mensaje: string;
  variables?: string[]; // Ej: ['{cliente}', '{producto}', '{precio}']
}

export const PLANTILLAS_WHATSAPP: PlantillaWhatsapp[] = [
  // INMOBILIARIA
  {
    id: 'inmobiliaria-bienvenida',
    emoji: '🏠',
    industria: 'Inmobiliaria',
    nombre: 'Bienvenida – Cliente nuevo',
    categoria: 'bienvenida',
    mensaje: `¡Hola {cliente}! 👋 

Soy el agente de {empresa}. Estamos aquí para ayudarte a encontrar tu próximo hogar.

¿Buscas comprar o arrendar? Te presento opciones que se ajusten a tu presupuesto. 🏠`,
    variables: ['{cliente}', '{empresa}'],
  },
  {
    id: 'inmobiliaria-oferta',
    emoji: '🏠',
    industria: 'Inmobiliaria',
    nombre: 'Oferta – Propiedad disponible',
    categoria: 'oferta',
    mensaje: `Encontramos una propiedad que te puede interesar 🎯

📍 {propiedad}
💰 {precio}
🛏️ {dormitorios} dorm | {banios} baños
📐 {m2} m²

¿Te gustaría ver fotos o agendar una visita?`,
    variables: ['{propiedad}', '{precio}', '{dormitorios}', '{banios}', '{m2}'],
  },
  {
    id: 'inmobiliaria-confirmacion',
    emoji: '🏠',
    industria: 'Inmobiliaria',
    nombre: 'Confirmación – Visita agendada',
    categoria: 'confirmacion',
    mensaje: `✅ Perfecto, {cliente}!

Tu visita está confirmada para:
📅 {fecha} a las {hora}
📍 {direccion}

Por favor llega 5 minutos antes. ¿Alguna pregunta sobre la propiedad?`,
    variables: ['{cliente}', '{fecha}', '{hora}', '{direccion}'],
  },

  // CLÍNICA / SALUD
  {
    id: 'clinica-bienvenida',
    emoji: '🩺',
    industria: 'Clínica / Salud',
    nombre: 'Bienvenida – Paciente nuevo',
    categoria: 'bienvenida',
    mensaje: `¡Hola {cliente}! 👋

Bienvenido a {empresa}. Soy tu asistente virtual y estoy aquí para ayudarte a agendar tu hora.

¿Qué prestación necesitas? (Consulta general, dental, etc.)`,
    variables: ['{cliente}', '{empresa}'],
  },
  {
    id: 'clinica-hora-disponible',
    emoji: '🩺',
    industria: 'Clínica / Salud',
    nombre: 'Oferta – Horas disponibles',
    categoria: 'oferta',
    mensaje: `Tenemos horas disponibles con {profesional} 📅

🗓️ {fecha1} a las {hora1}
🗓️ {fecha2} a las {hora2}

¿Cuál te acomoda? Confirma tu nombre completo y previsión.`,
    variables: ['{profesional}', '{fecha1}', '{hora1}', '{fecha2}', '{hora2}'],
  },
  {
    id: 'clinica-confirmacion',
    emoji: '🩺',
    industria: 'Clínica / Salud',
    nombre: 'Confirmación – Hora agendada',
    categoria: 'confirmacion',
    mensaje: `✅ ¡Tu hora está confirmada!

👨‍⚕️ Profesional: {profesional}
📅 Día: {fecha}
🕐 Hora: {hora}
📍 Consultorio: {consultorio}

Llega 10 minutos antes. Si necesitas cancelar, avísanos con 24h de anticipación.`,
    variables: ['{profesional}', '{fecha}', '{hora}', '{consultorio}'],
  },

  // RESTAURANTE
  {
    id: 'restaurante-bienvenida',
    emoji: '🍕',
    industria: 'Restaurante',
    nombre: 'Bienvenida – Pedido por WhatsApp',
    categoria: 'bienvenida',
    mensaje: `¡Hola {cliente}! 🍽️

Bienvenido a {empresa}. Puedo ayudarte a hacer tu pedido en minutos.

¿Qué te gustaría ordenar hoy?`,
    variables: ['{cliente}', '{empresa}'],
  },
  {
    id: 'restaurante-menu',
    emoji: '🍕',
    industria: 'Restaurante',
    nombre: 'Oferta – Menú del día',
    categoria: 'oferta',
    mensaje: `🌟 Nuestro menú de hoy:

1️⃣ {plato1} – {precio1}
2️⃣ {plato2} – {precio2}
3️⃣ {plato3} – {precio3}

Todos incluyen bebida y postre. ¿Cuál prefieres? 😋`,
    variables: ['{plato1}', '{precio1}', '{plato2}', '{precio2}', '{plato3}', '{precio3}'],
  },
  {
    id: 'restaurante-confirmacion',
    emoji: '🍕',
    industria: 'Restaurante',
    nombre: 'Confirmación – Pedido en preparación',
    categoria: 'confirmacion',
    mensaje: `✅ Tu pedido está confirmado 🍽️

📦 Pedido: {pedido}
💰 Total: {total}
🚚 Entrega en: {tiempo} minutos

Tu pedido está en la cocina. Te aviso cuando salga a despacho.`,
    variables: ['{pedido}', '{total}', '{tiempo}'],
  },

  // TIENDA DE ROPA
  {
    id: 'ropa-bienvenida',
    emoji: '👕',
    industria: 'Tienda de Ropa',
    nombre: 'Bienvenida – Compra en línea',
    categoria: 'bienvenida',
    mensaje: `¡Hola {cliente}! 👋

Bienvenido a {empresa}. Aquí encontrarás ropa de calidad y estilo.

¿Buscar algo en especial? (remeras, pantalones, etc.)`,
    variables: ['{cliente}', '{empresa}'],
  },
  {
    id: 'ropa-oferta',
    emoji: '👕',
    industria: 'Tienda de Ropa',
    nombre: 'Oferta – Descuento especial',
    categoria: 'oferta',
    mensaje: `🎉 ¡OFERTA ESPECIAL ESTA SEMANA!

{producto} – De {precio_original} a {precio_oferta}

✅ Stock disponible en tallas {tallas}

¿Te interesa? Cuéntame tu talla para reservar 👕`,
    variables: ['{producto}', '{precio_original}', '{precio_oferta}', '{tallas}'],
  },
  {
    id: 'ropa-seguimiento',
    emoji: '👕',
    industria: 'Tienda de Ropa',
    nombre: 'Seguimiento – Revisión de pedido',
    categoria: 'seguimiento',
    mensaje: `Hola {cliente}, ¿cómo te quedó el pedido? 📦

Nos gustaría saber tu opinión sobre {producto}.

Si tienes algún problema, estamos acá para ayudarte 💚`,
    variables: ['{cliente}', '{producto}'],
  },

  // PELUQUERÍA
  {
    id: 'peluqueria-bienvenida',
    emoji: '💇',
    industria: 'Peluquería',
    nombre: 'Bienvenida – Agendá tu turno',
    categoria: 'bienvenida',
    mensaje: `¡Hola {cliente}! ✨

Bienvenido a {empresa}. Aquí nos encanta transformar looks.

¿Qué servicio necesitas? (corte, color, tratamiento, etc.)`,
    variables: ['{cliente}', '{empresa}'],
  },
  {
    id: 'peluqueria-horas',
    emoji: '💇',
    industria: 'Peluquería',
    nombre: 'Oferta – Horas disponibles',
    categoria: 'oferta',
    mensaje: `Horas disponibles esta semana 📅

🕐 {fecha1} - {hora1} con {peluquero1}
🕐 {fecha2} - {hora2} con {peluquero2}

¿Cuál prefieres? Confirma tu nombre 💇‍♀️`,
    variables: ['{fecha1}', '{hora1}', '{peluquero1}', '{fecha2}', '{hora2}', '{peluquero2}'],
  },
  {
    id: 'peluqueria-recordatorio',
    emoji: '💇',
    industria: 'Peluquería',
    nombre: 'Recordatorio – Tu cita mañana',
    categoria: 'seguimiento',
    mensaje: `⏰ ¡No olvides tu cita mañana!

👤 Con: {peluquero}
🕐 Hora: {hora}
📍 Dirección: {direccion}

Estamos emocionados de verte. Si necesitas cambiar la hora, avísanos 💚`,
    variables: ['{peluquero}', '{hora}', '{direccion}'],
  },

  // EDUCACIÓN
  {
    id: 'educacion-bienvenida',
    emoji: '📚',
    industria: 'Educación / Academia',
    nombre: 'Bienvenida – Consulta de cursos',
    categoria: 'bienvenida',
    mensaje: `¡Hola {cliente}! 👋

Bienvenido a {empresa}. Aquí transformamos vidas a través de la educación 📚

¿En qué te gustaría capacitarte?`,
    variables: ['{cliente}', '{empresa}'],
  },
  {
    id: 'educacion-curso',
    emoji: '📚',
    industria: 'Educación / Academia',
    nombre: 'Oferta – Curso disponible',
    categoria: 'oferta',
    mensaje: `📖 Nuevo curso: {nombre_curso}

⏱️ Duración: {duracion}
💰 Valor: {precio}
📅 Inicio: {fecha_inicio}

Aprenderás: {contenido_breve}

¿Te interesa? Te dejo el link de inscripción 👇`,
    variables: ['{nombre_curso}', '{duracion}', '{precio}', '{fecha_inicio}', '{contenido_breve}'],
  },

  // TALLER MECÁNICO
  {
    id: 'taller-bienvenida',
    emoji: '🔧',
    industria: 'Taller Mecánico',
    nombre: 'Bienvenida – Revisión del auto',
    categoria: 'bienvenida',
    mensaje: `¡Hola {cliente}! 🚗

Bienvenido a {empresa}. Aquí cuidamos tu auto como si fuera el nuestro.

¿Qué necesita tu vehículo hoy?`,
    variables: ['{cliente}', '{empresa}'],
  },
  {
    id: 'taller-presupuesto',
    emoji: '🔧',
    industria: 'Taller Mecánico',
    nombre: 'Oferta – Presupuesto',
    categoria: 'oferta',
    mensaje: `💰 Presupuesto para tu {auto}

Trabajo: {descripcion}
💵 Valor: {valor}
⏱️ Tiempo: {tiempo}

¿Autorizas el trabajo? Confirma y agendamos 🔧`,
    variables: ['{auto}', '{descripcion}', '{valor}', '{tiempo}'],
  },
  {
    id: 'taller-listo',
    emoji: '🔧',
    industria: 'Taller Mecánico',
    nombre: 'Confirmación – Auto listo',
    categoria: 'confirmacion',
    mensaje: `✅ ¡Tu auto está listo! 🚗

Trabajo realizado: {trabajo}
💰 Total a pagar: {total}
🕐 Puedes venir a retirar en: {horario}

Gracias por confiar en nosotros 💚`,
    variables: ['{trabajo}', '{total}', '{horario}'],
  },

  // GIMNASIO / FITNESS
  {
    id: 'gimnasio-bienvenida',
    emoji: '💪',
    industria: 'Gimnasio / Fitness',
    nombre: 'Bienvenida – Conoce nuestros planes',
    categoria: 'bienvenida',
    mensaje: `¡Hola {cliente}! 💪

Bienvenido a {empresa}. Es hora de empezar tu transformación.

¿Qué tipo de entrenamiento te interesa? (musculación, cardio, crossfit, etc.)`,
    variables: ['{cliente}', '{empresa}'],
  },
  {
    id: 'gimnasio-plan',
    emoji: '💪',
    industria: 'Gimnasio / Fitness',
    nombre: 'Oferta – Planes de membresía',
    categoria: 'oferta',
    mensaje: `📋 Planes disponibles:

1️⃣ {plan1}: ${'{valor1}'}/mes - {beneficio1}
2️⃣ {plan2}: ${'{valor2}'}/mes - {beneficio2}
3️⃣ {plan3}: ${'{valor3}'}/mes - {beneficio3}

¿Cuál te interesa? Cuéntame más 🏋️`,
    variables: ['{plan1}', '{valor1}', '{beneficio1}', '{plan2}', '{valor2}', '{beneficio2}', '{plan3}', '{valor3}', '{beneficio3}'],
  },

  // HOTEL / HOSPEDAJE
  {
    id: 'hotel-bienvenida',
    emoji: '🏨',
    industria: 'Hotel / Hospedaje',
    nombre: 'Bienvenida – Reserva tu estadía',
    categoria: 'bienvenida',
    mensaje: `¡Hola {cliente}! 🏨

Bienvenido a {empresa}. Aquí ofrecemos confort y relajación.

¿Cuándo planeas visitarnos? Cuéntame las fechas 🗓️`,
    variables: ['{cliente}', '{empresa}'],
  },
  {
    id: 'hotel-disponibilidad',
    emoji: '🏨',
    industria: 'Hotel / Hospedaje',
    nombre: 'Oferta – Habitaciones disponibles',
    categoria: 'oferta',
    mensaje: `🛏️ Disponible para las fechas que buscas:

📍 {tipo_habitacion} - ${'{precio}'}/noche
✨ {beneficios}

✅ Desayuno incluido
✅ WiFi gratis
✅ Acceso a piscina

¿Reservamos? 🏖️`,
    variables: ['{tipo_habitacion}', '{precio}', '{beneficios}'],
  },

  // SERVICIOS GENERALES
  {
    id: 'servicios-bienvenida',
    emoji: '🛠️',
    industria: 'Servicios Generales',
    nombre: 'Bienvenida – Solicita tu servicio',
    categoria: 'bienvenida',
    mensaje: `¡Hola {cliente}! 👋

Bienvenido a {empresa}. Estamos listos para ayudarte.

¿Qué servicio necesitas? Cuéntame los detalles 🛠️`,
    variables: ['{cliente}', '{empresa}'],
  },
  {
    id: 'servicios-cotizacion',
    emoji: '🛠️',
    industria: 'Servicios Generales',
    nombre: 'Oferta – Cotización',
    categoria: 'oferta',
    mensaje: `📋 Cotización para tu solicitud:

Servicio: {servicio}
💰 Valor: {precio}
📅 Disponibilidad: {disponibilidad}

¿Te parece bien? Confirma y agendamos 👍`,
    variables: ['{servicio}', '{precio}', '{disponibilidad}'],
  },

  // FERRETERÍA / RETAIL
  {
    id: 'ferreteria-bienvenida',
    emoji: '🏗️',
    industria: 'Ferretería / Retail',
    nombre: 'Bienvenida – ¿Qué necesitas?',
    categoria: 'bienvenida',
    mensaje: `¡Hola {cliente}! 🏗️

Bienvenido a {empresa}. Aquí encontrarás todo para tu proyecto.

¿Qué artículo buscas hoy?`,
    variables: ['{cliente}', '{empresa}'],
  },
  {
    id: 'ferreteria-producto',
    emoji: '🏗️',
    industria: 'Ferretería / Retail',
    nombre: 'Oferta – Producto disponible',
    categoria: 'oferta',
    mensaje: `✅ Tenemos en stock:

📦 {producto}
💰 ${'{precio}'}
📍 Sucursal: {sucursal}
🚚 Envío gratis si compras más de ${'{monto_envio}'}

¿Lo apartas? ¿Necesitas otro artículo?`,
    variables: ['{producto}', '{precio}', '{sucursal}', '{monto_envio}'],
  },

  // VETERINARIA
  {
    id: 'veterinaria-bienvenida',
    emoji: '🐾',
    industria: 'Veterinaria',
    nombre: 'Bienvenida – Cuidado de mascotas',
    categoria: 'bienvenida',
    mensaje: `¡Hola {cliente}! 🐾

Bienvenido a {empresa}. Aquí cuidamos a tu mascota como si fuera nuestra.

¿Qué necesita tu {mascota}?`,
    variables: ['{cliente}', '{empresa}', '{mascota}'],
  },
  {
    id: 'veterinaria-cita',
    emoji: '🐾',
    industria: 'Veterinaria',
    nombre: 'Oferta – Cita disponible',
    categoria: 'oferta',
    mensaje: `📅 Cita disponible con el Dr. {veterinario}

🕐 {fecha} a las {hora}
🐾 Tipo: {tipo_servicio}
💰 Costo aprox: ${'{costo}'}

¿Confirmamos? Cuéntame el nombre y edad de tu mascota 🐾`,
    variables: ['{veterinario}', '{fecha}', '{hora}', '{tipo_servicio}', '{costo}'],
  },
];

/** Agrupa las plantillas por industria. */
export const PLANTILLAS_POR_INDUSTRIA = (): Record<string, PlantillaWhatsapp[]> => {
  const map: Record<string, PlantillaWhatsapp[]> = {};
  PLANTILLAS_WHATSAPP.forEach((p) => {
    if (!map[p.industria]) map[p.industria] = [];
    map[p.industria].push(p);
  });
  return map;
};
